import { useState, useEffect } from "react";
import { Accessibility, X, Type, Contrast, Link2, Minus, Plus, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";

interface AccessibilitySettings {
  fontSize: number;
  highContrast: boolean;
  blackWhite: boolean;
  highlightLinks: boolean;
  readableFont: boolean;
  reduceMotion: boolean;
  largeCursor: boolean;
}

const defaultSettings: AccessibilitySettings = {
  fontSize: 100,
  highContrast: false,
  blackWhite: false,
  highlightLinks: false,
  readableFont: false,
  reduceMotion: false,
  largeCursor: false,
};

const AccessibilityMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState<AccessibilitySettings>(defaultSettings);
  const [isVisible, setIsVisible] = useState(true);

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem("accessibility-settings");
    if (savedSettings) {
      try {
        setSettings(JSON.parse(savedSettings));
      } catch (error) {
        console.error("Error loading accessibility settings:", error);
      }
    }
  }, []);

  // Handle scroll to show/hide button
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      setIsVisible(false);
      
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setIsVisible(true);
      }, 150);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  // Apply settings to DOM
  useEffect(() => {
    const root = document.documentElement;

    // Font size
    root.style.fontSize = `${settings.fontSize}%`;

    // High contrast
    if (settings.highContrast) {
      root.classList.add("high-contrast");
    } else {
      root.classList.remove("high-contrast");
    }

    // Black and white
    if (settings.blackWhite) {
      root.classList.add("grayscale");
    } else {
      root.classList.remove("grayscale");
    }

    // Highlight links
    if (settings.highlightLinks) {
      root.classList.add("highlight-links");
    } else {
      root.classList.remove("highlight-links");
    }

    // Readable font
    if (settings.readableFont) {
      root.classList.add("readable-font");
    } else {
      root.classList.remove("readable-font");
    }

    // Reduce motion
    if (settings.reduceMotion) {
      root.classList.add("reduce-motion");
    } else {
      root.classList.remove("reduce-motion");
    }

    // Large cursor
    if (settings.largeCursor) {
      root.classList.add("large-cursor");
    } else {
      root.classList.remove("large-cursor");
    }

    // Save to localStorage
    localStorage.setItem("accessibility-settings", JSON.stringify(settings));
  }, [settings]);

  const updateSetting = <K extends keyof AccessibilitySettings>(
    key: K,
    value: AccessibilitySettings[K]
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const increaseFontSize = () => {
    setSettings((prev) => ({
      ...prev,
      fontSize: Math.min(prev.fontSize + 10, 150),
    }));
  };

  const decreaseFontSize = () => {
    setSettings((prev) => ({
      ...prev,
      fontSize: Math.max(prev.fontSize - 10, 80),
    }));
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
  };

  return (
    <>
      {/* Floating accessibility button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-4 right-4 z-50 bg-primary text-primary-foreground rounded-full p-3 shadow-lg hover:scale-110 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
        aria-label="תפריט נגישות"
      >
        <Accessibility className="h-6 w-6" />
      </button>

      {/* Accessibility menu panel */}
      {isOpen && (
        <div className="fixed bottom-20 right-4 z-50 bg-background border rounded-lg shadow-xl p-4 w-80 max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Accessibility className="h-5 w-5" />
              תפריט נגישות
            </h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              aria-label="סגור"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <Separator className="mb-4" />

          {/* Font size control */}
          <div className="mb-4">
            <Label className="flex items-center gap-2 mb-2">
              <Type className="h-4 w-4" />
              גודל טקסט ({settings.fontSize}%)
            </Label>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={decreaseFontSize}
                disabled={settings.fontSize <= 80}
                aria-label="הקטן טקסט"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <div className="flex-1 text-center text-sm">
                {settings.fontSize}%
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={increaseFontSize}
                disabled={settings.fontSize >= 150}
                aria-label="הגדל טקסט"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <Separator className="mb-4" />

          {/* Toggle options */}
          <div className="space-y-3">
            <button
              onClick={() => updateSetting("highContrast", !settings.highContrast)}
              className={`w-full p-3 rounded-md text-right transition-colors ${
                settings.highContrast
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary hover:bg-secondary/80"
              }`}
            >
              <div className="flex items-center justify-between">
                <span>ניגודיות גבוהה</span>
                <Contrast className="h-4 w-4" />
              </div>
            </button>

            <button
              onClick={() => updateSetting("blackWhite", !settings.blackWhite)}
              className={`w-full p-3 rounded-md text-right transition-colors ${
                settings.blackWhite
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary hover:bg-secondary/80"
              }`}
            >
              <div className="flex items-center justify-between">
                <span>מצב שחור-לבן</span>
                <Contrast className="h-4 w-4" />
              </div>
            </button>

            <button
              onClick={() => updateSetting("highlightLinks", !settings.highlightLinks)}
              className={`w-full p-3 rounded-md text-right transition-colors ${
                settings.highlightLinks
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary hover:bg-secondary/80"
              }`}
            >
              <div className="flex items-center justify-between">
                <span>הדגשת קישורים</span>
                <Link2 className="h-4 w-4" />
              </div>
            </button>

            <button
              onClick={() => updateSetting("readableFont", !settings.readableFont)}
              className={`w-full p-3 rounded-md text-right transition-colors ${
                settings.readableFont
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary hover:bg-secondary/80"
              }`}
            >
              <div className="flex items-center justify-between">
                <span>גופן קריא</span>
                <Type className="h-4 w-4" />
              </div>
            </button>

            <button
              onClick={() => updateSetting("reduceMotion", !settings.reduceMotion)}
              className={`w-full p-3 rounded-md text-right transition-colors ${
                settings.reduceMotion
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary hover:bg-secondary/80"
              }`}
            >
              <div className="flex items-center justify-between">
                <span>ביטול אנימציות</span>
                <RotateCcw className="h-4 w-4" />
              </div>
            </button>

            <button
              onClick={() => updateSetting("largeCursor", !settings.largeCursor)}
              className={`w-full p-3 rounded-md text-right transition-colors ${
                settings.largeCursor
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary hover:bg-secondary/80"
              }`}
            >
              <div className="flex items-center justify-between">
                <span>סמן עכבר גדול</span>
                <Plus className="h-4 w-4" />
              </div>
            </button>
          </div>

          <Separator className="my-4" />

          {/* Reset button */}
          <Button
            variant="outline"
            className="w-full"
            onClick={resetSettings}
          >
            <RotateCcw className="h-4 w-4 ml-2" />
            איפוס הגדרות
          </Button>
        </div>
      )}
    </>
  );
};

export default AccessibilityMenu;
